import React, { useState } from "react";
import {
    Alert,
    Box,
    Typography,
    Button,
    IconButton,
    Paper
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircularProgress from "@mui/material/CircularProgress";
import ExcelJS from "exceljs";
import { axios_tenant } from "../../../../../axios";

// ── Date validation helpers ────────────────────────────────────────────────

const MM_DD_YYYY = /^\d{2}\/\d{2}\/\d{4}$/;

function isValidMMDDYYYY(str) {
    if (!MM_DD_YYYY.test(str)) return false;
    const [mm, dd, yyyy] = str.split("/").map(Number);
    if (mm < 1 || mm > 12) return false;
    if (dd < 1 || dd > 31) return false;
    const d = new Date(yyyy, mm - 1, dd);
    return d.getMonth() === mm - 1 && d.getDate() === dd;
}

/**
 * Returns the string exactly as Excel displays it in the cell.
 * For plain-text cells: the raw string.
 * For date-serial cells: the date formatted using the cell's own numFmt,
 * so the user sees the same value here as in their spreadsheet.
 */
function getCellDisplayString(cell) {
    const raw = cell.value;
    if (raw === null || raw === undefined || raw === "") return "";

    if (raw instanceof Date) {
        const fmt = (cell.numFmt ?? "").toLowerCase();
        const mm   = String(raw.getMonth() + 1).padStart(2, "0");
        const dd   = String(raw.getDate()).padStart(2, "0");
        const yyyy = String(raw.getFullYear());

        if (fmt.includes("yyyy") && fmt.includes("mm") && fmt.includes("dd")) {
            return fmt
                .replace("yyyy", yyyy)
                .replace("mm",   mm)
                .replace("dd",   dd);
        }
        // numFmt absent or unrecognised — fall back to MM/DD/YYYY
        return `${mm}/${dd}/${yyyy}`;
    }

    return String(raw).trim();
}

/**
 * Reads the first worksheet, finds every column whose header contains "date"
 * (case-insensitive), and validates that each non-empty cell is MM/DD/YYYY.
 * Returns an array of error objects in the same shape the backend uses.
 */
/**
 * Scans rows to find the real header row — skips title/merged rows that don't
 * contain any recognisable column anchor ("File #" or a cell ending in "date").
 * Mirrors the backend's anchor-based findHeaderRow approach.
 */
function findHeaderRowNum(ws) {
    let found = -1;
    ws.eachRow((row, rowNum) => {
        if (found !== -1) return;
        let looksLikeHeader = false;
        let stop = false;
        row.eachCell((cell) => {
            if (stop) return;
            const val = String(cell.value ?? "").trim();
            // Anchor set mirrors backend HEADER_ANCHORS: "File #", "FIP Id"/"FIP ID".
            // Covers all supported flows: patent/trademark create → "File #",
            // patent update → "FIP Id"/"FIP ID". Trademark update is not supported.
            if (val === "File #" || val === "FIP Id" || val === "FIP ID") {
                looksLikeHeader = true;
                stop = true;
            }
        });
        if (looksLikeHeader) found = rowNum;
    });
    return found > 0 ? found : 1;
}

async function validateClientSide(file) {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await file.arrayBuffer());
    const ws = wb.getWorksheet(1);
    if (!ws) return [];

    const errors = [];

    const headerRowNum = findHeaderRowNum(ws);
    const headerRow = ws.getRow(headerRowNum);

    const dateCols = [];
    let fileNoColNum = -1;

    headerRow.eachCell((cell, colNum) => {
        const header = String(cell.value ?? "").trim();
        if (/\bdate\b/i.test(header)) dateCols.push({ colNum, header });
        if (header === "File #") fileNoColNum = colNum;
    });

    // Explicit anchor check — mirrors the backend's guard.
    // If neither a "File #" column nor any date column was found, the resolved
    // "header row" is not a real header (e.g. a merged title cell fell through).
    // Return early rather than relying on dateCols/fileNoColNum being empty.
    if (dateCols.length === 0 && fileNoColNum === -1) return [];

    // Duplicate detection is handled by the backend (/validate-file) which uses
    // the correct field name ("File #"). Client-side only adds date validation.
    ws.eachRow((row, rowNum) => {
        if (rowNum <= headerRowNum) return;

        let hasData = false;
        row.eachCell(() => { hasData = true; });
        if (!hasData) return;

        const fileNo = fileNoColNum > 0
            ? String(row.getCell(fileNoColNum).value ?? "").trim()
            : "";

        dateCols.forEach(({ colNum, header }) => {
            const cell = row.getCell(colNum);
            const displayStr = getCellDisplayString(cell);
            if (!displayStr) return;
            if (!isValidMMDDYYYY(displayStr)) {
                errors.push({
                    rowNumber: rowNum,
                    fileNo,
                    fieldName: header,
                    fieldValue: displayStr,
                    description: `Invalid date format. Required format: MM/DD/YYYY (e.g. 01/15/2024).`
                });
            }
        });
    });

    return errors;
}

// ── Component ──────────────────────────────────────────────────────────────

const UploadStep = ({ onNext, setValidationResult, file, setFile, configValues }) => {
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);

    const handleFile = (uploadedFile) => {
        setError(null);
        if (!uploadedFile.name.match(/\.(xls|xlsx)$/)) {
            alert("Only Excel files allowed");
            return;
        }
        if (uploadedFile.size > 3 * 1024 * 1024) {
            alert("Max size 3MB");
            return;
        }
        setFile(uploadedFile);
    };

    const handleFileChange = (e) => handleFile(e.target.files[0]);

    const handleDrop = (e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files[0]);
    };

    const handleDragOver = (e) => e.preventDefault();

    const removeFile = () => setFile(null);

    const validateExcelFile = async () => {
        if (!file) return;

        setLoading(true);
        setError(null);

        try {
            // Run client-side date validation and backend validation in parallel
            const formData = new FormData();
            formData.append("config", JSON.stringify(configValues));
            formData.append("file", file);

            const [clientErrors, backendRes] = await Promise.all([
                validateClientSide(file),
                axios_tenant.post("/adim/validate-file", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                })
            ]);

            const backendResult = backendRes.data;

            if (clientErrors.length === 0) {
                setValidationResult(backendResult);
            } else {
                const backendErrors = backendResult.errors ?? [];

                // Rows the backend already flagged as invalid
                const existingErrorRows = new Set(backendErrors.map(e => e.rowNumber));

                // Build a lookup from client errors: "rowNumber|fieldName" → fieldValue
                const clientFieldValues = {};
                clientErrors.forEach(e => {
                    const key = `${e.rowNumber}|${String(e.fieldName ?? "").toLowerCase()}`;
                    clientFieldValues[key] = e.fieldValue;
                });

                // Enrich backend errors with fieldValue from client when backend didn't provide one
                const enrichedBackendErrors = backendErrors.map(e => {
                    if (e.fieldValue != null && e.fieldValue !== "") return e;
                    const key = `${e.rowNumber}|${String(e.fieldName ?? "").toLowerCase()}`;
                    return key in clientFieldValues
                        ? { ...e, fieldValue: clientFieldValues[key] }
                        : e;
                });

                // Backend errors keyed by "rowNumber|fieldName" for dedup
                const backendErrorKeys = new Set(
                    backendErrors.map(e => `${e.rowNumber}|${String(e.fieldName ?? "").toLowerCase()}`)
                );

                // Drop client-side errors already reported by the backend for the same cell
                const uniqueClientErrors = clientErrors.filter(
                    e => !backendErrorKeys.has(`${e.rowNumber}|${String(e.fieldName ?? "").toLowerCase()}`)
                );

                // Rows newly invalid — not flagged by backend at all
                const newlyInvalidRows = new Set(
                    uniqueClientErrors
                        .filter(e => !existingErrorRows.has(e.rowNumber))
                        .map(e => e.rowNumber)
                );

                const mergedErrors = [...enrichedBackendErrors, ...uniqueClientErrors];

                setValidationResult({
                    ...backendResult,
                    errors:       mergedErrors,
                    errorCount:   mergedErrors.length,
                    validRecords: Math.max(0, (backendResult.validRecords ?? 0) - newlyInvalidRows.size)
                });
            }

            onNext();
        } catch (err) {
            console.error(err);
            setError("Failed to validate the file. Please check your file and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Paper sx={{ p: 4, background: "#fff" }}>
                <Typography variant="h5" sx={{ mb: 3 }}>
                    Upload File
                </Typography>

                <Box
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    sx={{
                        border: "2px dashed #c4c4c4",
                        borderRadius: 1,
                        height: 150,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        background: "#fafafa"
                    }}
                >
                    <input
                        id="excelUpload"
                        type="file"
                        accept=".xls,.xlsx"
                        hidden
                        onChange={handleFileChange}
                    />

                    <label htmlFor="excelUpload" style={{ cursor: "pointer" }}>
                        <UploadFileIcon color="primary" />
                        <Typography>
                            <span style={{ color: "#1976d2" }}>Click to upload</span> or drag and drop
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Only Excel File (max. 3MB)
                        </Typography>
                    </label>
                </Box>

                {file && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 3
                        }}
                    >
                        <Box>
                            <Typography>{file.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {(file.size / 1024).toFixed(0)} kb • Complete
                            </Typography>
                        </Box>

                        <Box>
                            <IconButton onClick={removeFile}>
                                <DeleteIcon />
                            </IconButton>
                            <CheckCircleIcon color="success" />
                        </Box>
                    </Box>
                )}
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                    variant="contained"
                    disabled={!file || loading}
                    onClick={validateExcelFile}
                >
                    {loading
                        ? <CircularProgress size={25} sx={{ color: "blue" }} />
                        : <span>Validate</span>
                    }
                </Button>
            </Box>
        </Box>
    );
};

export default UploadStep;
